import { Allowed_CSS_Properties, Allowed_HTML_Attributes, Allowed_HTML_Tags, Allowed_IDs } from "@ms/uno-constants/lib/local/DOMPurifyConstants";
import DOMPurify from "dompurify";
const EmptyStyleAttributePattern = /\sstyle\s*=\s*(['"])\s*\1/gi;
/**
 * Function used to sanitize content
 * @param content The original content being sanitized
 * @param customConfig Optional custom configuration for sanitization
 */ export function sanitizeContent(content, customConfig) {
    return sanitizeInternal(content, customConfig);
}
/**
 * Function used to sanitize content and return TrustedHTML if Trusted Types is enabled
 * @param content The original content being sanitized
 * @param customConfig Optional custom configuration for sanitization
 */ export function sanitizeForTrustedTypes(content, customConfig) {
    const config = customConfig ? {
        ...customConfig,
        returnTrustedTypes: true
    } : {
        returnTrustedTypes: true
    };
    return sanitizeInternal(content, config);
}
// Internal function to sanitize content with optional custom configuration
function sanitizeInternal(content, customConfig) {
    setUpDOMPurifyConfigs(customConfig);
    const sanitizedContent = DOMPurify.sanitize(content.replace(/&nbsp;/g, " "));
    removeDOMPurifyHooksAndConfig();
    return removeEmptyStyleAttributes(sanitizedContent);
}
function removeEmptyStyleAttributes(sanitizedContent) {
    // TrustedHTML cannot be manipulated as a string while preserving its trusted type.
    if (typeof sanitizedContent !== "string") {
        return sanitizedContent;
    }
    return sanitizedContent.replace(EmptyStyleAttributePattern, "");
}
// Helper to get a mapping of allowed css attributes to Javascript properties
function getAllowedCSSToJavascriptPropertiesMap() {
    const cssPropsMap = {};
    const toCamelCaseRegExp = new RegExp("-([a-z])", "g");
    Allowed_CSS_Properties.forEach((cssProp)=>{
        const cssJavaScriptProperty = cssProp.replace(toCamelCaseRegExp, function(g) {
            return g[1].toUpperCase();
        });
        cssPropsMap[cssJavaScriptProperty] = cssProp;
    });
    return cssPropsMap;
}
// Helper method to set up DOMPurify configs, including settings such as allowed attributes/tags etc.
function setUpDOMPurifyConfigs(customConfig) {
    const allowedCSSPropsMap = getAllowedCSSToJavascriptPropertiesMap();
    /**
     * Below code is mostly copied from: https://github.com/cure53/DOMPurify/blob/main/demos/hooks-sanitize-css-demo.html
     * The purpose of this chunk of code is to handling css sanitization
     * DOMPurify currently doesn't have a direct config to handle css sanitization
     */ /**
     * Take CSS property-value pairs and validate against allow-list,
     * then add the styles to an array of property-value pairs
     * Note: We also compare against original fullStyleString to ensure we're not adding CSS properties that didn't exist in original
     */ function validateStyles(output, styles, fullStyleString) {
        if (!styles || !fullStyleString) {
            return;
        }
        // We want case-insensitive comparison for CSS properties (and, "en-US" so we don't have incorrect lowercasing in other locales)
        fullStyleString = fullStyleString.toLocaleLowerCase("en-US");
        // Validate regular CSS properties
        for(const prop in styles){
            if (typeof styles[prop] === "string") {
                // Side Note: We can be stricter with the fullStyleString check (e.g., remove all whitespace from fullStyleString and check for `${css_prop}:`)
                //            but current approach suffices and better handles returning output closer to what user sees originally
                if (styles[prop] && allowedCSSPropsMap[prop] && fullStyleString.includes(allowedCSSPropsMap[prop])) {
                    output.push(allowedCSSPropsMap[prop] + ":" + styles[prop] + ";");
                }
            }
        }
    }
    // Add a hook to enforce CSS attribute sanitization
    DOMPurify.addHook("afterSanitizeAttributes", function(node) {
        // Remove all IDs that are not in the allowed list
        if (node.tagName && node.hasAttribute("id")) {
            const idValue = node.getAttribute("id");
            if (idValue !== null && !Allowed_IDs.includes(idValue)) {
                node.removeAttribute("id");
            }
        }
        // Nasty hack to fix baseURI + CSS problems in Chrome
        if (!node.ownerDocument.baseURI) {
            const base = document.createElement("base");
            base.href = document.baseURI;
            node.ownerDocument.head.appendChild(base);
        }
        // Check all style attribute values and validate them
        if (node.hasAttribute("style") && node.style != null) {
            const output = [];
            // We must cast node as any here because getAttribute("style") returns the style object as a flattened string
            validateStyles(output, node.style, node.getAttribute("style"));
            // re-add styles in case any are left
            if (output.length) {
                node.setAttribute("style", output.join(""));
            } else {
                node.removeAttribute("style");
            }
        }
    });
    /* END copied code */ // The following config settings specify accepted HTML tags and attributes
    // The accepted lists match with current Todo Server accepted list
    // Refer to service code todoserver/Utility/HtmlConverter.cs for allowed list
    // This setting also matches with Teams Tasks Mobile DomPurify Config setting
    // Refer to TT's mobilern/src/components/taskEditor/TaskRichTextEditor.tsx
    const config = {
        ALLOWED_TAGS: customConfig?.allowedTags ? [
            ...Allowed_HTML_Tags,
            ...customConfig.allowedTags
        ] : Allowed_HTML_Tags,
        ALLOWED_ATTR: customConfig?.allowedAttributes ? [
            ...Allowed_HTML_Attributes,
            ...customConfig.allowedAttributes
        ] : Allowed_HTML_Attributes,
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
        RETURN_TRUSTED_TYPE: customConfig?.returnTrustedTypes ?? false
    };
    DOMPurify.setConfig(config);
}
/**
 * Function used to remove DOMPurify hooks
 */ function removeDOMPurifyHooksAndConfig() {
    DOMPurify.removeHook("afterSanitizeAttributes");
    DOMPurify.clearConfig();
}
